import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import type { PricingGap, WaterPriceListCompleteness } from '@models/price-list-completeness';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { type CompletenessGapRow, type CompletenessState, gapLink } from './price-list-completeness.model';

/** How long the card survives the pointer or focus leaving, so either can cross onto the card itself. */
const CLOSE_DELAY_MS = 120;

/**
 * The "not fully priced" flag beside one price list, and the card naming what it is short of.
 *
 * A price list spans a grid per swim, a grid per accommodation and up to two lake-level grids, each of
 * them behind its own screen — so a hole in one of them is invisible from anywhere. This says which grid
 * to open, and links straight to it.
 *
 * Silent in two cases, which is deliberate. Until the count has been read there is nothing to claim
 * either way, and a lake that cannot be priced at all — no seasons configured, no capacity anywhere —
 * expects no cells, so it is not "missing" any: that gap belongs to the Season periods card, which says
 * it in those terms.
 *
 * The card holds links, so it cannot be a tooltip: it has to survive the pointer moving into it. Hence
 * the CDK overlay and the short close delay, and hence the trigger being a real button — keyboard focus
 * opens the same card and Tab reaches the links inside it, which is what {@link focusWithin} protects:
 * a pointer or focus leaving one part of the card only schedules a close, and focus arriving anywhere in
 * it — the flag, a link — vetoes one. Without that, tabbing from the flag to the first link would blur
 * the flag and close the card before the link could be reached.
 */
@Component({
  selector: 'app-price-list-completeness',
  templateUrl: './price-list-completeness.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OverlayModule, MatIconModule, MatTooltipModule, RouterLink, TranslatePipe],
})
export class PriceListCompletenessComponent {
  readonly waterId = input.required<number>();

  /** The price list's name, for the card's heading. */
  readonly priceListName = input.required<string>();

  /** Undefined until the completeness read lands, and after one that failed. */
  readonly completeness = input<WaterPriceListCompleteness | undefined>();

  private readonly translate = inject(TranslateService);

  readonly isOpen = signal(false);

  /** Whether the flag or something inside the card holds focus. While it does, nothing may close. */
  private readonly focusWithin = signal(false);

  readonly state = computed<CompletenessState>(() => {
    const completeness = this.completeness();
    if (completeness === undefined) return 'unknown';
    if (completeness.expectedCount === 0) return 'not-priceable';
    return completeness.missingCount === 0 ? 'complete' : 'incomplete';
  });

  readonly missingCount = computed(() => this.completeness()?.missingCount ?? 0);
  readonly expectedCount = computed(() => this.completeness()?.expectedCount ?? 0);

  readonly gapRows = computed<CompletenessGapRow[]>(() =>
    (this.completeness()?.gaps ?? []).map((gap) => ({
      key: `${gap.kind}:${gap.unitId ?? 0}`,
      label: this.gapLabel(gap),
      missingCount: gap.missingCount,
      expectedCount: gap.expectedCount,
      link: gapLink(gap, this.waterId()),
    })),
  );

  /** Below the flag, falling back to above it when the row sits near the bottom of the viewport. */
  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ];

  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.clearCloseTimer());
  }

  protected open(): void {
    this.clearCloseTimer();
    this.isOpen.set(true);
  }

  /**
   * Closes on a delay, so the pointer or the focus can cross the gap between the flag and the card.
   * Arriving on either cancels it, and a focused link vetoes it outright when it fires.
   */
  protected scheduleClose(): void {
    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      if (!this.focusWithin()) this.isOpen.set(false);
    }, CLOSE_DELAY_MS);
  }

  /** Focus landing on the flag or on anything in the card: show it, and hold it open. */
  protected onFocusIn(): void {
    this.focusWithin.set(true);
    this.open();
  }

  /**
   * Focus leaving one of them. It may be on its way to the other — Tab from the flag to the first link
   * blurs the flag before the link takes focus — so this only schedules the close.
   */
  protected onFocusOut(): void {
    this.focusWithin.set(false);
    this.scheduleClose();
  }

  /** Closes now: a link was followed, or the overlay was dismissed from outside. */
  protected close(): void {
    this.clearCloseTimer();
    this.focusWithin.set(false);
    this.isOpen.set(false);
  }

  private clearCloseTimer(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  /**
   * A swim is labelled by its number — its title defaults to the lake's, so it carries no meaning of its
   * own — while an accommodation has a real name. Both reuse the labels the pricing grids' copy-from
   * dropdown already uses, so the same unit reads the same way wherever it is offered.
   */
  private gapLabel(gap: PricingGap): string {
    switch (gap.kind) {
      case 'exclusive':
        return this.translate.instant('waters.price-lists.completeness.gap.exclusive');
      case 'raffle':
        return this.translate.instant('waters.price-lists.completeness.gap.raffle');
      case 'fishing-spot':
        return this.translate.instant('waters.fishing-spots.swim-label', { number: gap.unitNumber ?? gap.unitId });
      case 'accommodation':
        return this.translate.instant('waters.combination-prices.copy-accommodation-label', {
          name: gap.unitTitle ?? `#${gap.unitNumber ?? gap.unitId}`,
        });
    }
  }
}
